import React, { useState, useRef } from "react";
import {
    Box,
    Button,
    ChakraProvider,
    useToast,
    VStack,
    Heading,
    Text,
    Icon,
    Badge,
    Divider,
    Alert,
    AlertIcon,
    AlertDescription,
    Progress,
    HStack,
    Flex,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import NavigationMenu from "../Navigation/NavigationMenu";

const API_BASE = process.env.REACT_APP_BACKEND_API || "https://kidbackend.codingindia.co.in";

const JsonRestore = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);
    const toast = useToast();
    const navigate = useNavigate();

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.name.endsWith(".json")) {
                toast({
                    title: "Invalid File",
                    description: "Please select a valid .json backup file.",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                    position: "top-right",
                });
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file && file.name.endsWith(".json")) {
            setSelectedFile(file);
        } else {
            toast({
                title: "Invalid File",
                description: "Only .json backup files are accepted.",
                status: "warning",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
        }
    };

    const handleJsonRestore = async (e) => {
        e.preventDefault();

        if (!selectedFile) {
            toast({
                title: "No File Selected",
                description: "Please select a JSON backup file to restore.",
                status: "warning",
                duration: 3000,
                isClosable: true,
                position: "top-right",
            });
            return;
        }

        setIsLoading(true);
        const accessToken = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("file", selectedFile);

        try {
            const response = await fetch(
                `${API_BASE}/HumanResource/api/database/restore/json/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.error || `Server responded with ${response.status}`);
            }

            toast({
                title: "Restore Successful",
                description: "Database has been restored from the JSON backup file.",
                status: "success",
                duration: 6000,
                isClosable: true,
                position: "top-right",
            });

            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (error) {
            toast({
                title: "Restore Failed",
                description: error.message,
                status: "error",
                duration: 6000,
                isClosable: true,
                position: "top-right",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return "";
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    return (
        <>
            <NavigationMenu />
            <ChakraProvider>
                <Box minH="100vh" bg="gray.50" py={10} px={4}>
                    <Box maxW="680px" mx="auto">
                        {/* Header */}
                        <VStack spacing={2} mb={8} align="start">
                            <Badge colorScheme="orange" fontSize="sm" px={3} py={1} borderRadius="full">
                                JSON Restore
                            </Badge>
                            <Heading as="h1" size="xl" color="gray.800">
                                Restore from JSON Backup
                            </Heading>
                            <Text color="gray.500" fontSize="md">
                                Upload a previously downloaded JSON backup to restore your database records.
                            </Text>
                        </VStack>

                        <VStack spacing={5} align="stretch">
                            {/* Warning */}
                            <Alert status="warning" borderRadius="lg" border="1px" borderColor="orange.200">
                                <AlertIcon />
                                <AlertDescription fontSize="sm" color="orange.800">
                                    <strong>Warning:</strong> This will update existing records and insert new ones from the backup. Existing data may be overwritten. Only proceed with a trusted backup file.
                                </AlertDescription>
                            </Alert>

                            {/* File Drop Zone */}
                            <Box
                                bg="white"
                                borderRadius="xl"
                                p={6}
                                shadow="sm"
                                border="2px dashed"
                                borderColor={isDragging ? "orange.400" : selectedFile ? "green.400" : "gray.200"}
                                transition="all 0.2s"
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                                onDrop={handleDrop}
                                cursor="pointer"
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".json"
                                    onChange={handleFileChange}
                                    style={{ display: "none" }}
                                />
                                <VStack spacing={3} textAlign="center">
                                    <Icon viewBox="0 0 24 24" boxSize={10} color={selectedFile ? "green.500" : "gray.300"}>
                                        <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" />
                                    </Icon>
                                    {selectedFile ? (
                                        <VStack spacing={1}>
                                            <Text fontWeight="semibold" color="green.600" fontSize="md">
                                                {selectedFile.name}
                                            </Text>
                                            <Text fontSize="sm" color="gray.400">
                                                {formatFileSize(selectedFile.size)}
                                            </Text>
                                            <Badge colorScheme="green" fontSize="xs">Ready to restore</Badge>
                                        </VStack>
                                    ) : (
                                        <VStack spacing={1}>
                                            <Text fontWeight="medium" color="gray.600">
                                                Drag & drop your JSON backup here
                                            </Text>
                                            <Text fontSize="sm" color="gray.400">
                                                or click to browse files
                                            </Text>
                                            <Badge colorScheme="gray" fontSize="xs">.json files only</Badge>
                                        </VStack>
                                    )}
                                </VStack>
                            </Box>

                            {selectedFile && (
                                <Flex justify="flex-end">
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedFile(null);
                                            if (fileInputRef.current) fileInputRef.current.value = "";
                                        }}
                                    >
                                        ✕ Remove File
                                    </Button>
                                </Flex>
                            )}

                            <Divider />

                            {/* Restore Button */}
                            <Box bg="white" borderRadius="xl" p={6} shadow="sm" border="1px" borderColor="gray.100">
                                {isLoading && (
                                    <Box mb={4}>
                                        <Text fontSize="sm" color="gray.500" mb={2}>
                                            Restoring database, please do not close this page...
                                        </Text>
                                        <Progress size="xs" isIndeterminate colorScheme="orange" borderRadius="full" />
                                    </Box>
                                )}
                                <form onSubmit={handleJsonRestore}>
                                    <Button
                                        type="submit"
                                        colorScheme="orange"
                                        size="lg"
                                        w="full"
                                        isLoading={isLoading}
                                        loadingText="Restoring..."
                                        isDisabled={!selectedFile}
                                        leftIcon={
                                            <Icon viewBox="0 0 24 24" boxSize={5}>
                                                <path fill="currentColor" d="M12 8l-5 5 1.41 1.41L11 11.83V20h2v-8.17l2.59 2.58L17 13l-5-5zM6 4h12v2H6V4z" />
                                            </Icon>
                                        }
                                        borderRadius="lg"
                                        fontWeight="semibold"
                                        fontSize="md"
                                        py={6}
                                    >
                                        Restore JSON Data
                                    </Button>
                                </form>
                            </Box>

                            {/* Footer Actions */}
                            <HStack justify="space-between">
                                <Button
                                    variant="ghost"
                                    colorScheme="gray"
                                    onClick={() => navigate("/json-backup")}
                                    size="sm"
                                >
                                    ← Go to JSON Backup Page
                                </Button>
                                <Button
                                    variant="ghost"
                                    colorScheme="gray"
                                    onClick={() => navigate("/database-backup")}
                                    size="sm"
                                >
                                    SQL Backup & Restore →
                                </Button>
                            </HStack>
                        </VStack>
                    </Box>
                </Box>
            </ChakraProvider>
        </>
    );
};

export default JsonRestore;
